import { lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';

import { LazyWrapper } from '@/components/lazyWrapper';

import { ChatTest } from '@/pages/chattest';
import { DashboardLayout } from '@/pages/dashboard/components/layout';
import { SuggestedExperts } from '@/pages/dashboard/project/detail';
import { ProjectDetailLayout } from '@/pages/dashboard/project/detail/components/layout';

import { PrivateRoute } from '@/routes/private';
import { PublicRoute } from '@/routes/public';

// Auth
const SignIn = lazy(() => import('@/pages/auth').then((m) => ({ default: m.SignIn })));
const SignUp = lazy(() => import('@/pages/auth').then((m) => ({ default: m.SignUp })));
const TwoFactor = lazy(() => import('@/pages/auth').then((m) => ({ default: m.TwoFactor })));
const TwoFactorSetup = lazy(() => import('@/pages/auth').then((m) => ({ default: m.TwoFactorSetup })));
const ForgotPassword = lazy(() => import('@/pages/auth').then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('@/pages/auth').then((m) => ({ default: m.ResetPassword })));
const AcceptInvite = lazy(() => import('@/pages/auth').then((m) => ({ default: m.AcceptInvite })));
const SelectCompany = lazy(() => import('@/pages/auth').then((m) => ({ default: m.SelectCompany })));

// Dashboard
const Project = lazy(() => import('@/pages/dashboard').then((m) => ({ default: m.Project })));
const UserRoles = lazy(() => import('@/pages/dashboard').then((m) => ({ default: m.UserRoles })));
const Clients = lazy(() => import('@/pages/dashboard').then((m) => ({ default: m.Clients })));
const CreateRole = lazy(() => import('@/pages/dashboard/roles/components').then((m) => ({ default: m.CreateRole })));
// const UnallocatedProjects = lazy(() =>
//   import('@/pages/dashboard/Unallocated Project').then((m) => ({
//     default: m.default,
//   })),
// );
const AllExperts = lazy(() => import('@/pages/dashboard/experts').then((m) => ({ default: m.AllExperts })));
const OverviewDashboard = lazy(() =>
  import('@/pages/dashboard/overview/overview.dashboard').then((m) => ({ default: m.OverviewDashboard })),
);
const OverviewHistoryDashboard = lazy(() =>
  import('@/pages/dashboard/overview/overview-history.dashboard').then((m) => ({ default: m.OverviewHistoryDashboard })),
);
const EmployeeLeaderboardHistoryDashboard = lazy(() =>
  import('@/pages/dashboard/overview/employee-leaderboard-history.dashboard').then((m) => ({
    default: m.EmployeeLeaderboardHistoryDashboard,
  })),
);
const BestPerformerHistoryDashboard = lazy(() =>
  import('@/pages/dashboard/overview/best-performer-history.dashboard').then((m) => ({
    default: m.BestPerformerHistoryDashboard,
  })),
);
const ProjectsLaunchedHistoryDashboard = lazy(() =>
  import('@/pages/dashboard/overview/projects-launched-history.dashboard').then((m) => ({
    default: m.ProjectsLaunchedHistoryDashboard,
  })),
);
const SpeedOfDeliveryHistoryDashboard = lazy(() =>
  import('@/pages/dashboard/overview/speed-of-delivery-history.dashboard').then((m) => ({
    default: m.SpeedOfDeliveryHistoryDashboard,
  })),
);
const TeamLeadsPerformanceHistoryDashboard = lazy(() =>
  import('@/pages/dashboard/overview/teamleads-history.dashboard').then((m) => ({
    default: m.TeamLeadsPerformanceHistoryDashboard,
  })),
);
// Shared-link public URL page — disabled
// const SubmittedExpertsView = lazy(() =>
//   import('@/pages/submittedExpertsView/SubmittedExpertsView').then((m) => ({
//     default: m.SubmittedExpertsView,
//   })),
// );
const ExpertScreeningPublic = lazy(() =>
  import('@/pages/expertScreening/ExpertScreeningPublic').then((m) => ({
    default: m.default,
  })),
);
const ScreeningDecline = lazy(() =>
  import('@/pages/screeningDecline/ScreeningDecline').then((m) => ({
    default: m.default,
  })),
);
const TermsPage = lazy(() =>
  import('@/pages/legal/Terms').then((m) => ({
    default: m.default,
  })),
);
const PrivacyPage = lazy(() =>
  import('@/pages/legal/Privacy').then((m) => ({
    default: m.default,
  })),
);

const AddExpertForm = lazy(() =>
  import('@/pages/dashboard/project/detail/components/addExpert').then((m) => ({ default: m.AddExpertForm })),
);

const OverviewProjectDetail = lazy(() =>
  import('@/pages/dashboard/project/detail/overview.projectdetail').then((m) => ({ default: m.OverviewProjectDetail })),
);
// const InsightsProjectDetail = lazy(() =>
//   import('@/pages/dashboard/project/detail').then((m) => ({ default: m.InsightsProjectDetail })),
// );
const ExpertsProjectDetail = lazy(() =>
  import('@/pages/dashboard/project/detail').then((m) => ({ default: m.ExpertsProjectDetail })),
);
const TeamMembersProjectDetail = lazy(() =>
  import('@/pages/dashboard/project/detail').then((m) => ({ default: m.TeamMembersProjectDetail })),
);
// const ScheduleMeetingsProjectDetail = lazy(() =>
//   import('@/pages/dashboard/project/detail').then((m) => ({ default: m.ScheduleMeetingsProjectDetail })),
// );

// const ChatProjectDetail = lazy(() =>
//   import('@/pages/dashboard/project/detail/chat/chat.projectdetail').then((m) => ({ default: m.default })),
// );

const AddProject = lazy(() => import('@/pages/dashboard/project/components/addProject').then((m) => ({ default: m.AddProject })));

const SuggestedTenantProjectDetail = lazy(() =>
  import('@/pages/dashboard/project/detail/SuggestedTenant.projectdetail').then((m) => ({
    default: m.default,
  })),
);

// Shared project link landing — disabled
// const ProjectSharedLinkLanding = lazy(() =>
//   import('@/pages/projectSharedLink/ProjectSharedLinkLanding').then((m) => ({
//     default: m.ProjectSharedLinkLanding,
//   })),
// );

// Not FOund
const NotFound = lazy(() => import('@/pages/notFound/notFound'));

export const appRoutes = createBrowserRouter([
  {
    path: '/',
    element: <PrivateRoute />,
    children: [
      {
        path: '/',
        element: <DashboardLayout />,
        children: [
          {
            path: '/',
            element: (
              <LazyWrapper>
                <OverviewDashboard />
              </LazyWrapper>
            ),
          },
          {
            path: '/overview/history/:kpiKey',
            element: (
              <LazyWrapper>
                <OverviewHistoryDashboard />
              </LazyWrapper>
            ),
          },
          {
            path: '/overview/history/:kpiKey/:month',
            element: (
              <LazyWrapper>
                <OverviewHistoryDashboard />
              </LazyWrapper>
            ),
          },
          {
            path: '/overview/employee-leaderboard-history/:employeeId',
            element: (
              <LazyWrapper>
                <EmployeeLeaderboardHistoryDashboard />
              </LazyWrapper>
            ),
          },
          {
            path: '/overview/best-performer-history',
            element: (
              <LazyWrapper>
                <BestPerformerHistoryDashboard />
              </LazyWrapper>
            ),
          },
          {
            path: '/overview/projects-launched-history/:teamKey',
            element: (
              <LazyWrapper>
                <ProjectsLaunchedHistoryDashboard />
              </LazyWrapper>
            ),
          },
          {
            path: '/overview/speed-of-delivery-history',
            element: (
              <LazyWrapper>
                <SpeedOfDeliveryHistoryDashboard />
              </LazyWrapper>
            ),
          },
          {
            path: '/overview/team-leads-performance-history/:employeeId',
            element: (
              <LazyWrapper>
                <TeamLeadsPerformanceHistoryDashboard />
              </LazyWrapper>
            ),
          },

          {
            path: '/projects',
            element: (
              <LazyWrapper>
                <Project />
              </LazyWrapper>
            ),
            children: [
              {
                path: 'add-project',
                element: (
                  <LazyWrapper>
                    <AddProject />
                  </LazyWrapper>
                ),
              },
            ],
          },
          // {
          //   path: '/find-projects',
          //   element: (
          //     <LazyWrapper>
          //       <UnallocatedProjects />
          //     </LazyWrapper>
          //   ),
          // },

          {
            path: '/all-members',
            element: <Navigate to="/user-roles" replace />,
          },
          {
            path: '/roles',
            element: <Navigate to="/user-roles?tab=roles" replace />,
          },
          {
            path: '/user-roles',
            element: (
              <LazyWrapper>
                <UserRoles />
              </LazyWrapper>
            ),
          },
          {
            path: '/clients',
            element: (
              <LazyWrapper>
                <Clients />
              </LazyWrapper>
            ),
          },
          {
            path: '/experts',
            element: (
              <LazyWrapper>
                <AllExperts />
              </LazyWrapper>
            ),
          },
          {
            path: '/roles/create',
            element: (
              <LazyWrapper>
                <CreateRole />
              </LazyWrapper>
            ),
          },
          {
            path: 'experts/add-expert',
            element: (
              <LazyWrapper>
                <AddExpertForm />
              </LazyWrapper>
            ),
          },
          {
            path: 'experts/update-expert/:expertId',
            element: (
              <LazyWrapper>
                <AddExpertForm />
              </LazyWrapper>
            ),
          },
          {
            path: 'roles/update-role/:roleId',
            element: (
              <LazyWrapper>
                <CreateRole />
              </LazyWrapper>
            ),
          },
          {
            path: '/project-detail/:projectId',
            element: <ProjectDetailLayout />,
            children: [
              {
                path: 'overview',
                element: (
                  <LazyWrapper>
                    <OverviewProjectDetail />
                  </LazyWrapper>
                ),
              },
              // {
              //   path: 'insights',
              //   element: (
              //     <LazyWrapper>
              //       <InsightsProjectDetail />
              //     </LazyWrapper>
              //   ),
              // },
              {
                path: 'suggested-experts',
                element: (
                  <LazyWrapper>
                    <SuggestedExperts />
                  </LazyWrapper>
                ),
              },
              {
                path: 'experts',
                element: (
                  <LazyWrapper>
                    <ExpertsProjectDetail />
                  </LazyWrapper>
                ),
              },
              {
                path: 'experts/add',
                element: (
                  <LazyWrapper>
                    <AddExpertForm />
                  </LazyWrapper>
                ),
              },
              {
                path: 'team-members',
                element: (
                  <LazyWrapper>
                    <TeamMembersProjectDetail />
                  </LazyWrapper>
                ),
              },
              // {
              //   path: 'schedule-meetings',
              //   element: (
              //     <LazyWrapper>
              //       <ScheduleMeetingsProjectDetail />
              //     </LazyWrapper>
              //   ),
              // },
              // {
              //   path: 'chat',
              //   element: (
              //     <LazyWrapper>
              //       <ChatProjectDetail />
              //     </LazyWrapper>
              //   ),
              // },
              {
                path: 'suggested-tenant',
                element: (
                  <LazyWrapper>
                    <SuggestedTenantProjectDetail />
                  </LazyWrapper>
                ),
              },
            ],
          },
        ],
      },
      {
        path: '/select-company',
        element: (
          <LazyWrapper>
            <SelectCompany />
          </LazyWrapper>
        ),
      },
    ],
  },
  {
    path: '/auth',
    element: <PublicRoute />,
    children: [
      {
        path: 'signin',
        element: (
          <LazyWrapper>
            <SignIn />
          </LazyWrapper>
        ),
      },
      {
        path: 'signup',
        element: (
          <LazyWrapper>
            <SignUp />
          </LazyWrapper>
        ),
      },
      {
        path: 'two-factor',
        element: (
          <LazyWrapper>
            <TwoFactor />
          </LazyWrapper>
        ),
      },
      {
        path: 'two-factor-setup',
        element: (
          <LazyWrapper>
            <TwoFactorSetup />
          </LazyWrapper>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <LazyWrapper>
            <ForgotPassword />
          </LazyWrapper>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <LazyWrapper>
            <ResetPassword />
          </LazyWrapper>
        ),
      },
      {
        path: 'invite/:token',
        element: (
          <LazyWrapper>
            <AcceptInvite />
          </LazyWrapper>
        ),
      },
    ],
  },
  // Shared project link route — disabled
  // {
  //   path: '/project-link/:slug',
  //   element: (
  //     <LazyWrapper>
  //       <ProjectSharedLinkLanding />
  //     </LazyWrapper>
  //   ),
  // },
  // Public shared-experts route — disabled
  // {
  //   path: '/submitted-experts/:projectId/:ids/:token',
  //   element: (
  //     <LazyWrapper>
  //       <SubmittedExpertsView />
  //     </LazyWrapper>
  //   ),
  // },
  {
    path: '/expert-screening/:projectExpertId/:token',
    element: (
      <LazyWrapper>
        <ExpertScreeningPublic />
      </LazyWrapper>
    ),
  },
  {
    path: '/screening-decline',
    element: (
      <LazyWrapper>
        <ScreeningDecline />
      </LazyWrapper>
    ),
  },
  {
    path: '/terms',
    element: (
      <LazyWrapper>
        <TermsPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/privacy',
    element: (
      <LazyWrapper>
        <PrivacyPage />
      </LazyWrapper>
    ),
  },
  {
    path: '/chat-message',
    element: <ChatTest />,
  },
  {
    path: '*',
    element: (
      <LazyWrapper>
        <NotFound />
      </LazyWrapper>
    ),
  },

  {
    path: '/auth/invite/:token',
    element: (
      <LazyWrapper>
        <AcceptInvite />
      </LazyWrapper>
    ),
  },
]);
